import { Avatar, Box, Stack, Typography } from '@mui/material'
import DateRangeIcon from '@mui/icons-material/DateRange'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import KeyRoundedIcon from '@mui/icons-material/KeyRounded'
import ListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import FamilyIcon from '@mui/icons-material/FamilyRestroom'
import { FormatLongDate, BirthdayToAgeInYearsAndMonths } from 'core/Utilities'
import { type CaregiverMemberDetails } from 'Pages/CaregiverPortal/Members/CaregiverDisplay'

export interface CaregiverProfileCardProps {
  caregiver: CaregiverMemberDetails
}

export function CaregiverProfileCard (props: CaregiverProfileCardProps): JSX.Element {
  const { caregiver } = props

  const ethnicityDisplay = caregiver.isHispanic === true
    ? 'Hispanic or Latino'
    : caregiver.isHispanic === false
      ? 'Not Hispanic or Latino'
      : null
  const raceNames = caregiver.raceList.map(r => r.name)
  if (caregiver.tribalAffiliationStatus === true && caregiver.tribalAffiliationName != null) {
    raceNames.push(caregiver.tribalAffiliationName)
  }
  const raceDisplay = raceNames.join(', ')
  const raceEthnicityLine = [raceDisplay, ethnicityDisplay].filter(v => v != null && v !== '').join(' / ')
  const languageLine = caregiver.primaryLanguage != null && caregiver.primaryLanguage !== '' ? caregiver.primaryLanguage : 'English'

  return (
    <Box
      data-testid="caregiverProfileCard"
      sx={{ backgroundColor: '#ECEDEF', borderRadius: '10px', p: '20px 16px', display: 'flex', flexDirection: 'column', gap: '12px' }}
    >
      <Stack direction='row' spacing={1} alignItems='center'>
        <Avatar sx={{ height: '32px', width: '32px' }} />
        <Typography sx={{ color: '#222024', fontSize: '14px', fontWeight: 600 }} data-testid='caregiverNameTextDisplay'>
          {caregiver.firstName} {caregiver.lastName}
        </Typography>
      </Stack>

      {caregiver.dateOfBirth != null && (
        <Stack direction='row' spacing={2} alignItems='center'>
          <DateRangeIcon sx={{ color: '#222024' }} />
          <Typography sx={{ color: '#222024', fontSize: '14px' }} data-testid='dateOfBirthTextDisplay'>
            {FormatLongDate(caregiver.dateOfBirth)}
          </Typography>
          <Typography sx={{ color: '#514F53', fontSize: '12px' }}>
            ({BirthdayToAgeInYearsAndMonths(caregiver.dateOfBirth, false)})
          </Typography>
        </Stack>
      )}

      {caregiver.hasPortalAccess && (
        <Stack direction='row' spacing={2} alignItems='center'>
          <KeyRoundedIcon sx={{ color: '#222024' }} />
          <Typography sx={{ color: '#222024', fontSize: '14px' }}>Portal Access</Typography>
        </Stack>
      )}

      {caregiver.location != null && (
        <Stack direction='row' spacing={2} alignItems='flex-start'>
          <PlaceOutlinedIcon sx={{ color: '#222024' }} />
          <Box data-testid='addressTextDisplay'>
            <Typography sx={{ color: '#222024', fontSize: '14px' }}>{caregiver.location.address}</Typography>
            <Typography sx={{ color: '#222024', fontSize: '14px' }}>{caregiver.location.city}, {caregiver.location.state}</Typography>
            <Typography sx={{ color: '#222024', fontSize: '14px' }}>{caregiver.location.zipCode}</Typography>
          </Box>
        </Stack>
      )}

      {(raceEthnicityLine !== '' || languageLine !== '') && (
        <Stack direction='row' spacing={2} alignItems='flex-start'>
          <ListBulletedIcon sx={{ color: '#222024' }} />
          <Box>
            {raceEthnicityLine !== '' && <Typography sx={{ color: '#222024', fontSize: '14px' }}>{raceEthnicityLine}</Typography>}
            <Typography sx={{ color: '#222024', fontSize: '14px' }}>{languageLine}</Typography>
          </Box>
        </Stack>
      )}

      {caregiver.childRelationships.length > 0 && (
        <Stack direction='row' spacing={2} alignItems='flex-start'>
          <FamilyIcon sx={{ color: '#222024' }} />
          <Box data-testid='familyTextDisplay'>
            {caregiver.childRelationships.map((r, i) => (
              <Typography key={`${r.childName}-${i}`} sx={{ color: '#222024', fontSize: '14px' }}>
                {r.childName} - {r.relationshipName ?? '(none)'}
              </Typography>
            ))}
          </Box>
        </Stack>
      )}
    </Box>
  )
}
